// @file Network requests related to things on the padlet page
/* eslint-disable no-return-await, no-template-curly-in-string */
import { HTTPMethod } from '@padlet/fetch'
import { fetchUser, JsonAPIResponse, User as UserType } from '@padlet/arvo'
import template from 'lodash.template'
import { fetchJson, fetchJsonWithRetries } from 'surface/api_fetch'

const API_VERSION = 3
class FetchableObject {
  public static get url(): string {
    return ''
  }

  public static get fetchUrl(): string {
    return ''
  }

  public static buildUrl(url, options): string {
    return template(url)({ ...options, interpolate: /\${([\s\S]+?)}/g })
  }

  public static async readAll(options, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.fetchUrl, options), {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async read(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: JSON.stringify(obj),
      ...fetchOptions,
    })
  }

  public static async create(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}`, {
      method: HTTPMethod.post,
      body: JSON.stringify(obj),
      ...fetchOptions,
    })
  }

  public static async delete(obj, fetchOptions = {}): Promise<any> {
    return await fetchJson(`${this.url}/${obj.id}`, {
      method: HTTPMethod.delete,
      ...fetchOptions,
    })
  }
}

class Reaction extends FetchableObject {
  public static get url(): string {
    return '/reactions'
  }

  public static get fetchUrl(): string {
    return '/reactions?wall_id=${ wall.id }'
  }
}

class PostConnection extends FetchableObject {
  public static get url(): string {
    return '/api/wish_connections'
  }

  public static get fetchUrl(): string {
    return '/api/wish_connections?wall_id=${ wall.id }'
  }
}

class Comment extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/comments`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/comments?wall_id=\${ wall.id }`
  }
}

class WallSection extends FetchableObject {
  public static get url(): string {
    return '/wall_sections'
  }

  public static get fetchUrl(): string {
    return '/wall_sections?wall_id=${ wall.id }'
  }
}

// TODO: move to @padlet/arvo
class WallTableLayout extends FetchableObject {
  private static rowsEndpoint = '/api/wall_table_layout/rows'
  private static colsEndpoint = '/api/wall_table_layout/cols'
  private static rowAndColEndpoint = '/api/wall_table_layout/row_and_col'

  public static get fetchUrl(): string {
    return '/api/wall_table_layout?wall_id=${ wall.id }'
  }

  public static async createRow(obj): Promise<any> {
    return await fetchJson(this.rowsEndpoint, {
      method: HTTPMethod.post,
      body: JSON.stringify(obj),
    })
  }

  public static async createCol(obj): Promise<any> {
    return await fetchJson(this.colsEndpoint, {
      method: HTTPMethod.post,
      body: JSON.stringify(obj),
    })
  }

  public static async createRowAndCol(obj): Promise<any> {
    return await fetchJson(this.rowAndColEndpoint, {
      method: HTTPMethod.post,
      body: JSON.stringify(obj),
    })
  }

  public static async updateRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: JSON.stringify(obj),
    })
  }

  public static async updateCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.put,
      body: JSON.stringify(obj),
    })
  }

  public static async deleteRow(obj): Promise<any> {
    return await fetchJson(`${this.rowsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async deleteCol(obj): Promise<any> {
    return await fetchJson(`${this.colsEndpoint}/${obj.id}`, {
      method: HTTPMethod.delete,
    })
  }

  public static async putPostInCell(obj): Promise<any> {
    return await fetchJson('/api/wall_table_layout/cell', {
      method: HTTPMethod.post,
      body: JSON.stringify(obj),
    })
  }
}

class Wall extends FetchableObject {
  public static get url(): string {
    return '/walls'
  }

  private static get convertLayoutUrl(): string {
    return '/walls/${ id }/layout'
  }

  public static async convertLayout({ id, newLayout }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.convertLayoutUrl, { id }), {
      method: HTTPMethod.put,
      body: JSON.stringify({ type: newLayout }),
      ...fetchOptions,
    })
  }
}

class Wish extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/wishes`
  }

  public static get fetchUrl(): string {
    return `/api/${API_VERSION}/wishes?wall_id=\${ wall.id }&page_start=\${ pageStart }`
  }

  public static get transferUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/transfer`
  }

  public static get copyUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/copy`
  }

  public static get approveUrl(): string {
    return `/api/${API_VERSION}/wishes/\${ wish_id }/approve`
  }

  public static async transfer(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.transferUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: JSON.stringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
      }),
      ...fetchOptions,
    })
  }

  public static async copy(
    { wishId, wallId, wallSectionId, wallTitle, wallDescription, postPosition },
    fetchOptions = {},
  ): Promise<any> {
    return await fetchJson(this.buildUrl(this.copyUrl, { wish_id: wishId }), {
      method: HTTPMethod.post,
      body: JSON.stringify({
        wall_id: wallId,
        wall_section_id: wallSectionId,
        wall_title: wallTitle,
        wall_description: wallDescription,
        ...postPosition,
      }),
      ...fetchOptions,
    })
  }

  public static async approve({ wishId }, fetchOptions = {}): Promise<any> {
    return await fetchJson(this.buildUrl(this.approveUrl, { wish_id: wishId }), {
      method: HTTPMethod.put,
      body: JSON.stringify({}),
      ...fetchOptions,
    })
  }

  public static async update(obj, fetchOptions = {}): Promise<any> {
    return await fetchJsonWithRetries(`${this.url}/${obj.id}`, {
      method: HTTPMethod.put,
      body: JSON.stringify(obj),
      ...fetchOptions,
    })
  }
}

class WishOrder extends FetchableObject {
  public static get url(): string {
    return '/api/wish_order'
  }

  public static get fetchUrl(): string {
    return '/api/wish_order?wall_id=${ wall.id }'
  }
}

// UserContributor is read only
class UserContributor extends FetchableObject {
  public static get url(): string {
    return `/api/${API_VERSION}/users`
  }

  // The API WallContributorIds but it only return post author ids to match old behaviour for now
  public static async readWallContributorIds(wall, fetchOptions = {}): Promise<any> {
    return await fetchJson(`/api/${API_VERSION}/walls/${wall.id}/contributors`, {
      method: HTTPMethod.get,
      ...fetchOptions,
    })
  }

  public static read(userId: number): Promise<JsonAPIResponse<UserType>> {
    return fetchUser(userId)
  }
}

export default {
  Reaction: Reaction,
  PostConnection: PostConnection,
  Comment: Comment,
  WallSection: WallSection,
  WallTableLayout,
  Wall: Wall,
  Wish: Wish,
  WishOrder: WishOrder,
  UserContributor: UserContributor,
}
